import { ViewerHeader } from './ViewerHeader.js';
import { ViewerFooter } from './ViewerFooter.js'; // Engagement Hub
import { ViewerController } from './ViewerController.js'; // FAB
import { ViewerSettingsPanel } from './ViewerSettingsPanel.js';
import { ViewerResumeEngine } from './ViewerResumeEngine.js';
import { CopyrightWarning } from './CopyrightWarning.js';
// Comments are generally in `components/feature/comment/CommentSection.js` based on legacy mapping
import { CommentSection } from '../comment/CommentSection.js';
import "../../../lab/davinci/styles/viewer.css";

import { ContentService } from '../../../services/content.service.js';
import { DaVinciSanitizer } from '../../../lab/davinci/utils/DaVinciSanitizer.js';

/**
 * Da Vinci Viewer 3.0 (Refactored)
 * - Standardized Modules (Header, Footer, FAB)
 * - Resume Engine
 * - Optimistic Engagement
 */
export const DaVinciViewer = async (container, { blocks, metadata, user, navigation = {}, onTranslate }) => {
    // 1. Initial DOM
    container.innerHTML = `
        <div class="davinci-viewer-root" id="dv-root">
            <div class="dv-layer-atmosphere" id="dv-atmosphere"></div>
            
            <div class="dv-layer-content" id="dv-content">
                <!-- Title -->
                <!-- [FIX] Removed min-h-[40vh] to reduce whitespace -->
                <div class="dv-block visible flex flex-col items-center justify-center text-center p-8 pt-20 pb-10">
                    <h1 class="text-3xl md:text-4xl font-bold mb-4 font-serif text-white tracking-wide leading-tight">${metadata.title || '무제'}</h1>
                    ${metadata.authorNote ? `<p class="text-zinc-400 text-sm italic opacity-80">"${metadata.authorNote}"</p>` : ''}
                </div>

                <!-- Story Blocks -->
                <div id="dv-blocks-container"></div>

                <!-- Footer Slot (Replaces hardcoded footer) -->
                <div id="dv-footer-host"></div>
                
                <div class="h-32"></div>
            </div>
        </div>
    `;

    const root = container.querySelector('#dv-root');
    const atmosphereLayer = container.querySelector('#dv-atmosphere');
    const blocksContainer = container.querySelector('#dv-blocks-container');
    const footerHost = container.querySelector('#dv-footer-host');

    // [SEO & AI Metadata Injection]
    // Fetch AI Analysis (SEO/GEO) to enrich the page metadata
    try {
        // We need db instance. DaVinciViewer doesn't import it directly usually?
        // Let's import it or use ContentService if available. 
        // Direct Firestore is safest for metadata sub-collection.
        const { getFirestore, doc, getDoc } = await import("firebase/firestore");
        const { app } = await import("../../../firebase-config.js");
        const db = getFirestore(app);

        const aiMetaRef = doc(db, "series", navigation.seriesId, "episodes", navigation.episodeId, "ai_metadata", "seo_analysis");
        const aiMetaSnap = await getDoc(aiMetaRef);
        const aiData = aiMetaSnap.exists() ? aiMetaSnap.data() : null;

        // Dynamic Imports for SEO
        const { SEOManager } = await import('../../../utils/seo-manager.js');
        const { JsonLdInjector } = await import('../seo/JsonLdInjector.js');

        // 1. Basic Meta Tags
        SEOManager.updateMeta({
            title: `${metadata.title || 'Cine-Novel'} - ${metadata.seriesTitle || 'Ssulwha'}`,
            description: aiData?.hook || metadata.authorNote || "Watch this Cine-Novel on Ssulwha.",
            image: metadata.thumbnail || '/og.webp',
            keywords: aiData?.keywords ? aiData.keywords.join(',') : 'cinenovel,webtoon,story'
        });

        // 2. Advanced JSON-LD (Schema.org) with GEO Data
        const locationContext = aiData?.locations?.real ? aiData.locations.real : [];

        JsonLdInjector.inject({
            mode: 'article',
            title: metadata.title,
            description: aiData?.hook || metadata.authorNote || "Watch this Cine-Novel on Ssulwha.",
            author: metadata.authorId || "Unknown",
            datePublished: new Date().toISOString(), // Approximation
            image: metadata.thumbnail,
            // [AI GEO Injection]
            ocrContext: {
                locations: locationContext,
                vibes: aiData?.keywords || []
            }
        });

        console.log("[DaVinci] SEO Injected:", { hasAI: !!aiData, locations: locationContext });

    } catch (e) {
        console.warn("[DaVinci] SEO Injection Failed (Non-critical):", e);
    }

    // [PHASE 2] TTS Decoupling Strategy
    // Initialize Controller & TTS *BEFORE* rendering blocks to prevent "Image First" race condition.
    // This ensures TTS content is generated from data immediately.

    // [FIX] TTS Sanitization Logic
    const generateTTSContent = (blockList) => {
        return blockList
            .filter(b => b.type === 'text')
            .map(b => DaVinciSanitizer.stripForTTS(b.content))
            .filter(text => text.length > 0) // Remove empty lines
            .join(' '); // Use space instead of newline for smooth flowing TTS
    };

    const scrollTarget = (container && container !== document.body) ? container : window;

    const controller = new ViewerController(container, {
        viewerType: 'davinci',
        bgmUrl: metadata.bgm,
        content: generateTTSContent(blocks), // [FIX] Sanitized Text
        onTranslate: onTranslate, // [New] Pass through translation handler
        onScrollTop: () => {
            // [FIX] Scroll both targets to ensure it works in Fullscreen (Window) and Preview (Popup Container)
            window.scrollTo({ top: 0, behavior: 'smooth' });
            if (scrollTarget && scrollTarget.scrollTo) {
                scrollTarget.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    });

    // 2. Render Blocks
    blocks.forEach(block => {
        const el = document.createElement('div');
        el.className = 'dv-block';
        el.dataset.id = block.id;
        el.dataset.type = block.type;

        if (block.type === 'text') {
            // Determine if content is Rich Text (HTML) or Legacy (Plain Text)
            if (DaVinciSanitizer.isHtml(block.content)) {
                // [SECURITY] Sanitize HTML to prevent XSS
                el.innerHTML = `<div class="dv-text-container">${DaVinciSanitizer.sanitize(block.content)}</div>`;
            } else {
                // Legacy Fallback: Convert \n to <br> or <p> safely
                // Special Dialogue Styling (Viewer-side visual only)
                const lines = block.content.split('\n');
                const html = lines.map(line => {
                    const trimmed = line.trim();
                    if (!trimmed) return '<br>';
                    const isDialogue = trimmed.startsWith('"') || trimmed.startsWith('“');
                    // Escape each line individually
                    const safeLine = DaVinciSanitizer.convertLegacyToHtml(line).replace('<br>', ''); // remove br added by helper
                    return `<p class="dv-paragraph ${isDialogue ? 'type-dialogue' : ''}">${safeLine}</p>`;
                }).join('');
                el.innerHTML = `<div class="dv-text-container">${html}</div>`;
            }
        } else if (block.type === 'image') {
            el.innerHTML = `
                <div class="dv-image-wrapper">
                    <img src="${block.url}" class="dv-image" loading="lazy" alt="Scene" oncontextmenu="return false" draggable="false">
                </div>`;
        } else if (block.type === 'atmosphere') {
            el.style.height = '1px';
            el.style.margin = '0';
            el.dataset.effect = block.content;
        }
        blocksContainer.appendChild(el);
    });

    // 3. Scrollytelling
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('visible');
                if (entry.target.dataset.type === 'atmosphere') {
                    const effect = entry.target.dataset.effect;
                    if (effect === 'dark') atmosphereLayer.style.background = '#000';
                    else if (effect === 'warm') atmosphereLayer.style.background = 'radial-gradient(circle, #3e2723 0%, #000 100%)';
                    else if (effect === 'rainy') atmosphereLayer.style.background = 'linear-gradient(to bottom, #1f2937, #111827)';
                }
            }
        });
    }, { threshold: 0 }); // [FIX] Threshold 0 to ensure long blocks are visible immediately
    container.querySelectorAll('.dv-block').forEach(b => observer.observe(b));

    // 4. Module Integration

    // A. Settings (Logic First)
    const settingsPanel = new ViewerSettingsPanel({
        uid: user?.uid,
        onSettingsChange: (settings) => {
            if (settings.fontSize) root.style.setProperty('--dv-font-size', `${settings.fontSize / 16}rem`);
            if (settings.theme) {
                // [FIX] Also update popup background to ensure seamless theme transition
                // This prevents the black popup background from being visible behind the viewer
                const popup = document.getElementById('davinci-preview-popup');

                if (settings.theme === 'theme-black') {
                    root.style.setProperty('--dv-bg', '#000000');
                    root.style.setProperty('--dv-text-primary', '#cccccc');
                    if (popup) popup.style.backgroundColor = '#000000';
                } else if (settings.theme === 'theme-sepia') {
                    root.style.setProperty('--dv-bg', '#1f1c18');
                    root.style.setProperty('--dv-text-primary', '#d7ccc8');
                    if (popup) popup.style.backgroundColor = '#1f1c18';
                } else if (settings.theme === 'theme-white') {
                    // Added White Theme Support explicitly
                    root.style.setProperty('--dv-bg', '#ffffff');
                    root.style.setProperty('--dv-text-primary', '#111111');
                    if (popup) popup.style.backgroundColor = '#ffffff';
                } else { // Default (Dark)
                    root.style.setProperty('--dv-bg', '#121212');
                    root.style.setProperty('--dv-text-primary', '#e0e0e0');
                    if (popup) popup.style.backgroundColor = '#121212';
                }
            }
        }
    });

    // B. Header
    const header = new ViewerHeader(container, {
        title: metadata.title,
        viewerType: 'davinci',
        // [FIX] Use injected onBack if available (for Preview Close), else default history.back
        onBack: navigation.onBack || (() => window.history.back()),
        // [FIX] Pass custom back icon if provided (e.g., 'close' for preview)
        backIcon: navigation.backIcon,
        onSettings: () => settingsPanel.toggle(),
        onSearch: () => {
            // Dispatch global search event or import SearchModal if possible
            // window.dispatchEvent(new CustomEvent('header-action', { detail: 'toggleSearch' }));
            import('../../search-modal.js').then(({ SearchModal }) => SearchModal.show());
        }
    });
    // Header binding logic is now internal to ViewerHeader (auto-attaches listener)

    // C. Footer
    const footer = new ViewerFooter(footerHost, {
        seriesId: navigation.seriesId,
        episodeId: navigation.episodeId,
        seriesData: metadata, // Pass full metadata for ISBN/UCI/Likes
        hasPrev: navigation.hasPrev,
        hasNext: navigation.hasNext,
        onPrev: navigation.onPrev,
        onNext: navigation.onNext
    });

    // D. Rating Component (Inject into Footer Slot)
    const ratingSlot = footerHost.querySelector('#vf-rating-slot');
    if (ratingSlot) {
        import('./RatingComponent.js').then(({ RatingComponent }) => {
            RatingComponent(ratingSlot, {
                seriesId: navigation.seriesId,
                episodeId: navigation.episodeId,
                initialRating: metadata.stats?.ratingAverage || 0,
                initialCount: metadata.stats?.ratingCount || 0
            });
        });
    }

    // E. Comment Section (Inject into Footer Slot)
    const commentSlot = footerHost.querySelector('#vf-comment-slot');
    if (commentSlot) {
        CommentSection(commentSlot, {
            seriesId: navigation.seriesId,
            episodeId: navigation.episodeId,
            currentUser: user,
            authorId: metadata.authorId,
            hasNext: navigation.hasNext,
            onNext: navigation.onNext
        });
    }

    // E. FAB Controller
    // [FIX] Scroll Target Logic: If rootElement (popup) is provided, scroll that. Else window.
    // [re-fix] 'options' was undefined. Use 'container' (which is the popup in preview) 
    // or verify if 'navigation.rootElement' was passed. 
    // In DaVinciEditor, we pass `rootElement: popup` but it was destructured away.
    // Safest fallback: Use `container` as it IS the popup element in Preview Mode.
    // Safest fallback: Use `container` as it IS the popup element in Preview Mode.
    // const scrollTarget moved up

    // [moved up] Controller should be initialized BEFORE blocks to prevent race condition
    // Force icon refresh for FAB
    setTimeout(() => {
        if (controller && controller.refreshIcons) controller.refreshIcons();
    }, 500);

    // 5. Security & Continuity

    // Resume Engine
    ViewerResumeEngine.init(window, {
        seriesId: navigation.seriesId,
        episodeId: navigation.episodeId,
        title: metadata.title,
        thumbnail: metadata.thumbnail,
        seriesTitle: metadata.seriesTitle || metadata.title
    }, { episodeIndex: metadata.episodeIndex || 0 });

    // [FIX] Log View Count
    try {
        ContentService.logView(navigation.seriesId, navigation.episodeId, user ? user.uid : 'guest');
    } catch (e) {
        console.warn("[Viewer] Failed to log view:", e);
    }

    return {
        // [New] Dynamic Content Update for Translation
        updateContent: (newBlocks, newMetadata) => {
            if (newMetadata && newMetadata.title) {
                container.querySelector('h1').textContent = newMetadata.title;
            }

            if (newBlocks) {
                blocksContainer.innerHTML = ''; // Clear
                // Refactored Render Logic (Inline for now to avoid breaking scope of existing loop vars)
                newBlocks.forEach(block => {
                    const el = document.createElement('div');
                    el.className = 'dv-block';
                    el.dataset.id = block.id;
                    el.dataset.type = block.type;

                    if (block.type === 'text') {
                        if (DaVinciSanitizer.isHtml(block.content)) {
                            el.innerHTML = `<div class="dv-text-container">${DaVinciSanitizer.sanitize(block.content)}</div>`;
                        } else {
                            const lines = block.content.split('\n');
                            const html = lines.map(line => {
                                const trimmed = line.trim();
                                if (!trimmed) return '<br>';
                                const isDialogue = trimmed.startsWith('"') || trimmed.startsWith('“');
                                const safeLine = DaVinciSanitizer.convertLegacyToHtml(line).replace('<br>', '');
                                return `<p class="dv-paragraph ${isDialogue ? 'type-dialogue' : ''}">${safeLine}</p>`;
                            }).join('');
                            el.innerHTML = `<div class="dv-text-container">${html}</div>`;
                        }
                    } else if (block.type === 'image') {
                        el.innerHTML = `
                <div class="dv-image-wrapper">
                    <img src="${block.url}" class="dv-image" loading="lazy" alt="Scene" 
                         oncontextmenu="return false" 
                         draggable="false" 
                         style="-webkit-touch-callout: none; user-select: none;">
                </div>`;
                    } else if (block.type === 'atmosphere') {
                        el.style.height = '1px';
                        el.style.margin = '0';
                        el.dataset.effect = block.content;
                    }
                    blocksContainer.appendChild(el);
                    observer.observe(el); // Re-attach observer
                });

                // [FIX] Update TTS Content Dynamically
                if (controller && controller.updateContent) {
                    controller.updateContent(generateTTSContent(newBlocks));
                }
            }
        },
        destroy: () => {
            // [FIX] Cleanup Header to prevent Zombie state
            if (header && header.destroy) header.destroy();

            if (controller && controller.destroy) controller.destroy();
            if (window.activeViewerController === controller) window.activeViewerController = null;

            settingsPanel.destroy();
            ViewerResumeEngine.destroy(window);
        }
    };
};
